import type React from "react"
import { useState } from "react"
import { useLocation } from "react-router-dom"
import { SidebarNav } from "./sidebar-nav"
import { TopBar } from "./top-bar"
import { AgentWidget } from "./agent-widget"
import { FloatingAgentButton } from "./floating-agent-button"
import { DashboardRenderer } from "./dashboard-renderer"
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "components/ui/sheet"

interface CurrentUser {
  id?: number
  first_name?: string
  last_name?: string
  email?: string
  username?: string
  role?: string
  is_superuser?: boolean
  language?: string
  working_hours?: string
  profile_photo_url?: string | null
}

interface AppShellProps {
  currentUser?: CurrentUser | null
  children?: React.ReactNode
  onUserUpdate?: (user: CurrentUser) => void
}

export function AppShell({ currentUser, children, onUserUpdate }: AppShellProps) {
  const location = useLocation()
  const isDashboard = location.pathname === "/dashboard-new"
  const [navigationOpen, setNavigationOpen] = useState(false)

  return (
    <div
      className="dashboard-v0 relative min-h-screen overflow-clip"
      style={{
        background: `linear-gradient(to bottom right, var(--hexa-bg-gradient-start), var(--hexa-bg-gradient-mid), var(--hexa-bg-gradient-end))`,
      }}
    >
      {/* Decorative purple ellipse glow */}
      <div
        className="absolute -top-32 -right-32 w-[450px] h-[450px] rounded-full pointer-events-none"
        style={{
          background: "var(--hexa-glow-purple)",
          filter: "blur(120px)",
        }}
      />

      {/* Main layout */}
      <div className="relative flex min-h-screen">
        {/* Sidebar */}
        <SidebarNav currentUser={currentUser} className="hidden min-h-screen lg:flex" />

        <Sheet open={navigationOpen} onOpenChange={setNavigationOpen}>
          <SheetContent side="left" className="w-56 max-w-56 gap-0 border-0 bg-transparent p-0 lg:hidden">
            <SheetTitle className="sr-only">Navigazione principale</SheetTitle>
            <SheetDescription className="sr-only">Vai a una sezione di Hexa</SheetDescription>
            <SidebarNav currentUser={currentUser} className="h-full w-full" onNavigate={() => setNavigationOpen(false)} />
          </SheetContent>
        </Sheet>

        {/* Main content area */}
        <main className="min-w-0 flex-1 flex flex-col">
          <TopBar currentUser={currentUser} onUserUpdate={onUserUpdate} onMenuOpen={() => setNavigationOpen(true)} />

          {/* Main content */}
          <div className="min-w-0 flex-1">
            {children || <div className="p-3 sm:p-6 max-w-7xl w-full mx-auto"><DashboardRenderer currentUser={currentUser} /></div>}
          </div>
        </main>
      </div>

      {isDashboard ? (
        <FloatingAgentButton
          isOpen={false}
          label="Vai alla chat"
          onClick={() => {
            const chat = document.getElementById("dashboard-agent-chat")
            chat?.scrollIntoView({ behavior: "smooth", block: "center" })
            chat?.querySelector("textarea")?.focus({ preventScroll: true })
          }}
        />
      ) : <AgentWidget />}
    </div>
  )
}
